<script setup lang="ts">
import InputGroup from '@/components/Auths/InputGroup.vue'
import ButtonLoading from '@/components/Buttons/ButtonLoading.vue'
import { InputFromField } from '@/interfaces/components/inputFromField'
import { IInterruptionAlarmSensor } from '@/interfaces/entities/alarm_profile'
import yup from '@/plugins/yup'
import { useAlarmProfileStore } from '@/store/alarmProfile'
import { useErrorStore } from '@/store/error'
import { ref, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { ValidationError } from 'yup'
import OutlineButton from '@/components/Globals/Buttons/OutlineButton.vue'

const { t } = useI18n()
const profileStore = useAlarmProfileStore()
const isLoading = ref(false)

const props = defineProps<{
  currentProfile: IInterruptionAlarmSensor | null,
  formType: 'create' | 'edit',
}>()

const profile = ref<IInterruptionAlarmSensor | null>(null)

const emit = defineEmits(['cancel'])

const currentSettings = props.currentProfile?.settings


const inputs: Ref<InputFromField[]> = ref([
  {
    label: 'Nome',
    placeholder: 'Informe o nome do perfil',
    value: props.formType === 'edit' ? props.currentProfile?.name ?? profile.value?.name ?? '' : '',
  },
  {
    label: 'alarms.min_current',
    placeholder: 'alarms.current_message',
    value: props.formType === 'edit' ? currentSettings?.interrupt_current_setpoint ?? '' : '',
  },
  {
    label: 'alarms.time_min_current',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.interrupt_time_setpoint ?? '' : '',
  },
  {
    label: 'alarms.min_debouncing',
    placeholder: 'alarms.inform_time.debouncing',
    value: props.formType === 'edit' ? currentSettings?.debouncing_time_setpoint ?? '' : '',
  },
])

const alarmSettingsSchema = yup.object({
  name: yup
  .string()
  .required('O campo nome é obrigatório'),
  interrupt_current_setpoint: yup
    .number()
    .typeError('O campo da corrente de interrupção deve ser um número')
    .required('O campo da corrente de interrupção é obrigatório')
    .min(0, 'O campo da corrente de interrupção não pode ser menor que zero'),
  interrupt_time_setpoint: yup
    .number()
    .typeError('O campo do tempo de interrupção deve ser um número')
    .required('O campo do tempo de interrupção é obrigatório')
    .min(0, 'O campo do tempo de interrupção não pode ser menor que zero'),
  debouncing_time_setpoint: yup
    .number()
    .typeError('O campo do tempo de debouncing deve ser um número')
    .required('O campo do tempo de debouncing é obrigatório')
    .min(0, 'O campo do tempo de debouncing não pode ser menor que zero'),
})

async function handleSubmit() {
  const values: IInterruptionAlarmSensor = {
    name: String(inputs.value[0].value),
    interrupt_current_setpoint: Number(inputs.value[1].value),
    interrupt_time_setpoint: Number(inputs.value[2].value),
    debouncing_time_setpoint: Number(inputs.value[3].value),
  }
  try {
    await alarmSettingsSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    if(props.formType === 'create'){
      await profileStore.createInterruptionAlarmProfile(values)
    } else if(props.formType === 'edit' && props.currentProfile){
      await profileStore.editInterruptionAlarmProfile(props.currentProfile.id, values)
    }
    isLoading.value = false
    emit('cancel')
  } catch (err) {
    if (err instanceof ValidationError) {
      useErrorStore().setErrors('Erro ao criar perfil: ', err.errors)
    }
  }
}
</script>

<template>
  <div class="border border-stroke bg-gray-2 p-5 shadow-sm">
    <div class="flex flex-wrap gap-4">
      <span v-if="props.formType ==='create'" class="w-full text-base font-medium text-gray-500 mb-1">
        {{ "Criar perfil de Interrupção" }}
      </span>
      <div v-if="props.formType === 'edit'" class="w-full flex justify-items-center">
        <span class="w-full text-base font-medium text-gray-500 mb-1">
          {{ "Editar perfil: " + (props.currentProfile?.name || '') }}
        </span>
      </div>
      <div class="w-full border-t border-stroke pt-1"></div>
      <template v-for="(input, index) in inputs" :key="index">
        <div class="min-w-[200px] w-full">
          <InputGroup v-model="input.value" :label="t(input.label)" :placeholder="t(input.placeholder)" />
        </div>
      </template>

      <div class="flex w-full justify-end gap-2">
        <OutlineButton 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap"
          @click="emit('cancel')"
          label="Cancelar" />
        <ButtonLoading 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
          :isLoading="isLoading"
          @submit="handleSubmit"
          label="Salvar" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  :deep(label) {
    color: #64748b;
    font-size: 0.875rem;
  }
  :deep(input) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
</style>
